import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Fab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import ConsultationTypeFormModal from '../consultationTypeFormModal';
import { useAppointmentTypes } from '../../../hooks/useAppointmentTypes';
import { useDeleteConsultationType } from '../hooks/useDeleteConsultationType';

function ConsultationTypeListMobile() {
  const navigate = useNavigate();

  const {
    appointmentTypes: consultationTypes,
    isLoading,
    error: listError,
    refetch: getConsultationTypes,
  } = useAppointmentTypes();
  const { remove } = useDeleteConsultationType();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedConsultationType, setSelectedConsultationType] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConsultationTypeId, setSelectedConsultationTypeId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, consultationTypeId) => {
    setAnchorEl(event.currentTarget);
    setSelectedConsultationTypeId(consultationTypeId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedConsultationType(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const type = consultationTypes.find((t) => t.id === selectedConsultationTypeId);
    if (type) {
      setSelectedConsultationType(type);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedConsultationType(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedConsultationTypeId(null);
  };

  const handleConsultationTypeSaved = () => {
    getConsultationTypes();
    setSuccessMessage('Tipo de consulta salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedConsultationTypeId)
      .then((res) => {
        getConsultationTypes();
        setSuccessMessage(res.data ?? 'Tipo de consulta excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir tipo de consulta.');
        if (err.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedConsultationTypeId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
          <CardHeader
            title="Tipos de Consulta"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'text.secondary' }}
          />
          <CardContent>
            <List>
              {consultationTypes.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum tipo de consulta cadastrado
                </Typography>
              ) : (
                consultationTypes.map((consultationType) => (
                  <React.Fragment key={consultationType.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, consultationType.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={consultationType.description} />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Fab
        color="primary"
        aria-label="adicionar tipo de consulta"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={openCreateModal}
      >
        <AddIcon />
      </Fab>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={openEditModal}>Editar</MenuItem>
        <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <ConsultationTypeFormModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleConsultationTypeSaved}
        consultationType={selectedConsultationType}
      />

      <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja excluir este tipo de consulta? Essa ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmModal}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ConsultationTypeListMobile;
